import React from "react";
import { Modal, Button } from "antd";
import moment from "moment";
import { MdLock } from "react-icons/md";
import { IoWarning } from "react-icons/io5";

interface Props {
  type: "expired" | "expiring_soon" | "none";
  visible: boolean;
  daysLeft: number;
  endDate: string | null;
  onClose: () => void;
  onContinue?: () => void;
  onRenew: () => void;
}

function SubscriptionModal({
  type,
  visible,
  daysLeft,
  endDate,
  onClose,
  onContinue,
  onRenew,
}: Props) {
  const isExpired = type === "expired";
  const isNone = type === "none";
  const formattedDate = endDate ? moment(endDate).format("DD MMM YYYY") : "";

  const primaryColor = isNone
    ? "#0576b7"
    : isExpired
    ? "#c62828"
    : "#e65100";
  const bgLight = isNone
    ? "#e8f4fb"
    : isExpired
    ? "#ffebee"
    : "#fff3e0";

  const handleRenew = () => {
    onClose();
    onRenew();
  };

  return (
    <Modal
      open={visible}
      footer={null}
      closable
      onCancel={onClose}
      maskClosable={!isExpired && !isNone}
      centered
      width={420}
      styles={{ mask: { backdropFilter: "blur(2px)" } }}
    >
      <div style={{ textAlign: "center", padding: "24px 12px 8px" }}>
        <div
          style={{
            width: 80,
            height: 80,
            borderRadius: "50%",
            background: bgLight,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            margin: "0 auto 18px",
          }}
        >
          {isExpired || isNone ? (
            <MdLock size={44} color={primaryColor} />
          ) : (
            <IoWarning size={44} color={primaryColor} />
          )}
        </div>

        <div
          style={{
            fontSize: 20,
            fontFamily: "NotoSansMalayalam-Bold",
            color: primaryColor,
            marginBottom: 10,
          }}
        >
          {isNone
            ? "Subscription Required"
            : isExpired
            ? "Subscription Expired"
            : "Subscription Expiring Soon"}
        </div>

        <div
          style={{
            fontSize: 14,
            fontFamily: "NotoSansMalayalam-Regular",
            color: "#555",
            lineHeight: 1.7,
            marginBottom: 14,
          }}
        >
          {isNone ? (
            <>
              You don't have an active subscription. Please subscribe to read
              the ePaper.
            </>
          ) : isExpired ? (
            <>
              Your subscription has expired. Please renew your subscription to
              continue reading the ePaper.
            </>
          ) : (
            <>
              Your subscription expires on{" "}
              <strong>{formattedDate}</strong>. Renew now to avoid
              interruption.
            </>
          )}
        </div>

        {!isNone && (
          <div
            style={{
              display: "inline-block",
              background: bgLight,
              color: primaryColor,
              borderRadius: 20,
              padding: "5px 16px",
              fontSize: 13,
              fontFamily: "NotoSansMalayalam-SemiBold",
              marginBottom: 24,
            }}
          >
            {isExpired
              ? `Expired on: ${formattedDate}`
              : daysLeft === 1
              ? "1 day remaining"
              : `${daysLeft} days remaining`}
          </div>
        )}

        <div
          style={{
            display: "flex",
            gap: 12,
            justifyContent: "center",
          }}
        >
          {!isExpired && !isNone && onContinue && (
            <Button
              size="large"
              onClick={() => {
                onClose();
                onContinue();
              }}
              style={{
                borderColor: "#0576b7",
                color: "#0576b7",
                fontFamily: "NotoSansMalayalam-SemiBold",
                minWidth: 140,
              }}
            >
              Continue Reading
            </Button>
          )}
          <Button
            type="primary"
            size="large"
            onClick={handleRenew}
            style={{
              background: "#0576b7",
              borderColor: "#0576b7",
              fontFamily: "NotoSansMalayalam-SemiBold",
              minWidth: 140,
            }}
          >
            {isNone ? "Subscribe Now" : "Renew Subscription"}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

export default SubscriptionModal;
